import React, { useState, useEffect } from "react";
import {
  Send,
  Eye,
  Copy,
  CheckCircle,
  Clock,
  AlertCircle,
  X,
  Check,
  Info,
  AlertTriangle,
  MailIcon,
  Loader2,
  Image as ImageIcon,
  FileText,
  Trash2,
  PencilIcon,
  ScanEyeIcon,
  DownloadIcon,
} from "lucide-react";
import ApplicationTracker from "./ApplicationTracker";
import {
  ConfirmationModal,
  Modal,
  ModalBody,
  ModalFooter,
} from "../Modal/MainModal";
import { useAuth } from "../context/AuthContext";

// Toast Component
export const Toast = ({ message, type, onClose }) => {
  const getToastStyles = () => {
    const baseStyles =
      "fixed top-4 right-4 z-50 flex items-center p-4 rounded-lg shadow-lg max-w-sm w-full transform transition-all duration-300 ease-in-out";
    switch (type) {
      case "success":
        return `${baseStyles} bg-green-50 border border-green-200 text-green-800`;
      case "error":
        return `${baseStyles} bg-red-50 border border-red-200 text-red-800`;
      case "warning":
        return `${baseStyles} bg-yellow-50 border border-yellow-200 text-yellow-800`;
      case "info":
        return `${baseStyles} bg-blue-50 border border-blue-200 text-blue-800`;
      default:
        return `${baseStyles} bg-gray-50 border border-gray-200 text-gray-800`;
    }
  };

  const getIcon = () => {
    switch (type) {
      case "success":
        return <Check className="w-5 h-5 text-green-500 mr-3" />;
      case "error":
        return <AlertCircle className="w-5 h-5 text-red-500 mr-3" />;
      case "warning":
        return <AlertTriangle className="w-5 h-5 text-yellow-500 mr-3" />;
      case "info":
        return <Info className="w-5 h-5 text-blue-500 mr-3" />;
      default:
        return <Info className="w-5 h-5 text-gray-500 mr-3" />;
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={getToastStyles()}>
      {getIcon()}
      <div className="flex-1">
        <p className="text-sm font-medium">{message}</p>
      </div>
      <button
        onClick={onClose}
        className="ml-3 text-gray-400 hover:text-gray-600"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Toast Hook
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = "info") => {
    const id = Date.now();
    const toast = { id, message, type };
    setToasts((prev) => [...prev, toast]);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return { toasts, showToast, removeToast };
};

const HROnboardingPanel = () => {
  const [formData, setFormData] = useState({
    offerLetterRef: "",
    firstName: "",
    middleName: "",
    lastName: "",
    mobile: "",
    email: "",
    remark: "",
  });
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toasts, showToast, removeToast } = useToast();

  // Load data from API on component mount
  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/pre-onboarding`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSubmissions(data);
      } else {
        showToast("Failed to load onboarding data", "error");
      }
    } catch (error) {
      console.error("Error fetching submissions:", error);
      showToast("Error connecting to server", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    // Basic validation
    if (
      !formData.offerLetterRef ||
      !formData.firstName ||
      !formData.lastName ||
      !formData.mobile ||
      !formData.email
    ) {
      showToast("Please fill in all required fields", "warning");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showToast("Please enter a valid email address", "warning");
      return;
    }

    setIsSubmitting(true);

    try {
      let payload = {
        offer_letter_reference: formData.offerLetterRef,
        first_name: formData.firstName,
        middle_name: formData.middleName || "",
        last_name: formData.lastName,
        full_name: `${formData.firstName} ${
          formData.middleName ? formData.middleName + " " : ""
        }${formData.lastName}`.trim(),
        mobile_number: formData.mobile,
        email_address: formData.email,
        date_of_joining: new Date().toISOString().split("T")[0],
        verification_code: Math.floor(100000 + Math.random() * 900000),
        remark: formData.remark,
      };

      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/pre-onboarding`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        const data = await response.json();
        showToast("Onboarding entry created successfully!", "success");

        // Reset form
        setFormData({
          offerLetterRef: "",
          firstName: "",
          middleName: "",
          lastName: "",
          mobile: "",
          email: "",
          remark: "",
        });

        // Refresh submissions
        await fetchSubmissions();
      } else {
        const errorData = await response.json();
        showToast(
          errorData.message || "Failed to create onboarding entry",
          "error"
        );
      }
    } catch (error) {
      console.error("Error creating onboarding entry:", error);
      showToast("Error connecting to server. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast("Reference copied to clipboard!", "success");
    } catch (error) {
      console.log(error);
      showToast("Failed to copy to clipboard", "error");
    }
  };

  const getStatusIcon = (status) => {
    const percentage = parseInt(status);
    if (percentage === 100) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    } else if (percentage > 50) {
      return <Clock className="w-4 h-4 text-blue-500" />;
    } else if (percentage > 0) {
      return <Clock className="w-4 h-4 text-yellow-500" />;
    }
    return <AlertCircle className="w-4 h-4 text-red-500" />;
  };

  const getStatusBadge = (status) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium gap-1";
    const percentage = parseInt(status);

    if (percentage === 100) {
      return `${baseClasses} bg-green-100 text-green-800`;
    } else if (percentage > 50) {
      return `${baseClasses} bg-blue-100 text-blue-800`;
    } else if (percentage > 0) {
      return `${baseClasses} bg-yellow-100 text-yellow-800`;
    }
    return `${baseClasses} bg-red-100 text-red-800`;
  };

  const getStatusText = (status) => {
    const percentage = parseInt(status);
    if (percentage === 100) return "Completed";
    if (percentage > 50) return "In Progress";
    if (percentage > 0) return "Started";
    return "Not Started";
  };
  const [eid, setEid] = useState("");

  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleConfirm = () => {
    setIsOpen(false);
    // Add your confirmation logic here
  };
  const [sendEmailLoading, setSendEmailLoading] = useState(false);

  const sendToMail = async (eid, name, email, verificationCode) => {
    try {
      setEid(eid);
      setSendEmailLoading(true);
      const mailResp = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/email/send-mail`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify({
            eid,
            name,
            verificationCode,
            to_email: email,
            subject: "Welcome to Kajaria!",
            content: `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Welcome to Kajaria</title>
</head>
<body style="background:#f4f6fb;margin:0;font-family:Inter, 'Helvetica Neue', Arial, sans-serif;color:#293241;">
  <table role="presentation" cellspacing="0" cellpadding="0" width="100%" style="max-width:680px;margin:0 auto;background:#ffffff;border-radius:10px;overflow:hidden;box-shadow:0 6px 18px rgba(20,30,60,0.08);">
    <tr>
      <td style="padding:20px 28px;border-bottom:1px solid #2b67a3;">
        <!-- Header: logos inline -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <td style="vertical-align:middle">
              <img src="https://hrms.kajariaceramics.com/images/logo.png" alt="Kajaria" width="110" style="display:inline-block;vertical-align:middle;">
            </td>
            <td style="text-align:right;vertical-align:middle">
              <!-- Additional partner logos (optional) -->
              <img src="https://www.kerovit.com/kerovit_logo.png" alt="" width="120" style="display:inline-block;vertical-align:middle;margin-left:12px; filter: brightness(0) saturate(100%) invert(15%) sepia(90%) saturate(1200%) hue-rotate(200deg) brightness(100%) contrast(95%);" >
              <img src="https://www.gresbondbykajaria.com/assets/images/logo-dark.webp" alt="" width="120" style="display:inline-block;vertical-align:middle;margin-left:12px;">
            </td>
          </tr>
        </table>
      </td>
    </tr>

    <tr>
      <td style="padding:28px;">
        <!-- Greeting -->
        <h1 style="margin:0 0 8px;font-size:20px;color:#0b3b66;">Welcome to Kajaria, ${name}</h1>
        <p style="margin:0 0 18px;color:#505c6b;line-height:1.5;">
          Dear ${name},<br><br>
          We’re excited to have you join our team. To get started, please complete your onboarding steps using the Document Management
           Portal.
        </p>

        <!-- Offer reference card -->
        <div style="background:#f6fbff;border:1px solid #e6f0fb;padding:12px 14px;border-radius:8px;margin-bottom:18px; display: flex;">
          <strong style="display:block;color:#0b3b66;margin-bottom:6px">Your Offer Letter Reference:- </strong>
          <div style="font-size:16px;color:#0b3b66;font-weight:600;">${eid}</div>
        </div>

        <!-- Steps -->
        <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="margin-bottom:18px;">
          <tr>
            <td style="padding:8px 0;">
              <strong style="color:#0b3b66;display:block;margin-bottom:6px">Quick Start — What to do next</strong>

              <ol style="margin:0;padding-left:20px;color:#555;line-height:1.6;">
                <li><strong>Open the portal:</strong> Click the button below or go to <a href="https://hrms.kajariaceramics.com" style="color:#1453b6;text-decoration:none;">https://hrms.kajariaceramics.com</a>.</li>
                <li><strong>Create an account:</strong> Click <em>Create Account</em> and enter your offer letter reference: <code style="background:#f1f5fb;padding:2px 6px;border-radius:4px;border:1px solid #e6eefb;">${eid}</code>.</li>
                <li><strong>Verify:</strong> Use below verification code to verify your account.</li>
                <li><strong>Set password:</strong> Create a secure password — you'll use this or your email/reference ID to sign in.</li>
                <li><strong>Complete forms:</strong> Fill the Joining Form, Personal Information, and ID Card Form from the dashboard sidebar.</li>
              </ol>
            </td>
          </tr>
        </table>

        <!-- CTA button -->
        <div style="text-align:center;margin:6px 0 22px;">
            <a href="https://hrms.kajariaceramics.com" target="_blank"
               style="display:inline-block;padding:12px 22px;border-radius:8px;background:#1453b6;color:#fff;text-decoration:none;font-weight:600;box-shadow:0 6px 18px rgba(20,80,160,0.12);">
              Access the Document Management Portal
            </a>
        </div>

        <!-- OTP block (if you want to include an OTP in the mail) -->
        <div style="background:#f8fafc;border:1px solid #eef4ff;padding:14px;border-radius:8px;margin-bottom:18px; display: flex; align-items: center;">
          <div style="font-size:13px;color:#718096;margin-bottom:6px;">Verification Code: </div>
          <div style="display:flex;align-items:center;gap:12px;">
            <div style="font-weight:700;color:#0b3b66;font-size:24px;letter-spacing:2px;padding:8px 14px;background:#fff;border-radius:6px;border:1px solid #e6eefb; margin-left: 8px; align-items: center;">${verificationCode}</div>
            <div style="color:#8a9bb0;font-size:13px; margin-left: 8px">Use this code to verify your account.</div>
          </div>
        </div>

        <!-- Helpful notes -->
        <p style="margin:0 0 16px;color:#55606b;line-height:1.5;">
          After completing the forms, HR team will review your details and contact you with the next steps. If you face any issues, reach out to <a href="mailto:corporate_hr@kajariaceramics.com" style="color:#1453b6;text-decoration:none;">corporate_hr@kajariaceramics.com</a>.
        </p>

        <!-- Footer CTA small -->
        <div style="text-align:center;margin-top:18px;">
          <a href="https://hrms.kajariaceramics.com" style="font-size:13px;color:#1453b6;text-decoration:none;">Open the onboarding portal</a>
        </div>
      </td>
    </tr>

    <tr>
      <td style="background:#fbfdff;padding:16px 28px;border-top:1px solid #eef2f6;">
        <table role="presentation" width="100%">
          <tr>
            <td style="font-size:13px;color:#7b8794;">
              © <strong>Kajaria</strong> — All rights reserved.<br>
              <span style="color:#9aa7b4;">Need help? Contact <a href="mailto:corporate_hr@kajariaceramics.com" style="color:#1453b6;">corporate_hr@kajariaceramics.com</a></span>
            </td>
            <td style="text-align:right">
              <a href="https://hrms.kajariaceramics.com" style="color:#1453b6;text-decoration:none;font-size:13px;">Visit Portal</a>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
`,
          }),
        }
      );
      if (mailResp.ok) {
        const data = await mailResp.json();
        showToast("Mail sent successfully!", "success");
      } else {
        showToast("Failed to send mail", "error");
      }
      setSendEmailLoading(false);
    } catch (error) {
      console.log(error);
      showToast("Failed to send mail", "error");
      setSendEmailLoading(false);
    }
  };

  const [confirmModalDetails, setConfirmModalDetails] = useState({
    isOpen: false,
    title: "Confirm Action",
    message: "Are you sure you want to proceed?",
    confirmText: "Confirm",
    cancelText: "Cancel",
    type: "warning",
    onConfirm: () => {},
    onClose: () => {},
  });

  const [modalDetails, setModalDetails] = useState({
    isOpen: false,
    title: "Confirm Action",
    message: "Are you sure you want to proceed?",
    confirmText: "Confirm",
    cancelText: "Cancel",
    type: "warning",
    onConfirm: () => {},
    onClose: () => {},
    data: null,
  });

  const [documentPreviewModal, setDocumentPreviewModal] = useState({
    isOpen: false,
    title: "Document Preview",
    message: "Are you sure you want to proceed?",
    confirmText: "Proceess",
    cancelText: "Cancel",
    type: "info",
    eid: "",
    onConfirm: () => {},
    onClose: () => {},
  });

  const handleModal=(modalDetails)=>{
    setConfirmModalDetails(modalDetails);
  }

  const handleUpdate=async (refId)=>{
    const empId = document.getElementById("EmployeeId").value;
    if(!empId){
      showToast("Please enter employee id", "error");
      setModalDetails({ isOpen: false });
      return;
    }
    const response = await fetch(
      `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/update-employee-id?user_id=${refId}&employee_id=${empId}`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        }
      }
    );
    if (!response.ok) {
      const errorData = await response.json(); // parse FastAPI error body
      const message =
        errorData?.detail ||
        `Failed to update applications: ${response.status} ${response.statusText}`;
      showToast(message, "error");
      setModalDetails({ isOpen: false });
      return;
    }
    const data = await response.json();
    showToast("Applications updated successfully!", "success");
    setModalDetails({ isOpen: false });
    fetchSubmissions();
  }

  const [mergedFile, setMergedFile] = useState(null)
  const mergeAndUpload = async (eid) => {
    if (!mergedFile) return;
    try {
      const formData = new FormData();
      formData.append("file", mergedFile);
  
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/upload-document`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: formData,
      });
  
      if (!response.ok) {
        throw new Error("Upload failed");
      }
  
      return response.json();
    } catch (error) {
      console.log(error);
      showToast("Failed to merge and upload documents", "error");
      setModalDetails({ isOpen: false });
    }
  }

  const handleDownloadAll = async (eid) => {
    try {
      const resp = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/emp-documnets/docs/merge?eid=${eid}`, {
        method: 'GET'
      })
      if (!resp.ok) {
        showToast(resp.statusText, 'error');
        let errorDetail = await resp.json();
        console.log(errorDetail)
        throw new Error(errorDetail.detail);
      }
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${eid}_merged.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      showToast('All documents downloaded successfully!', 'success');
    } catch (error) {
      console.error('Download error:', error);
      showToast(error.message, 'error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {/* Toast Notifications */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}
      <ConfirmationModal
        isOpen={confirmModalDetails.isOpen}
        onClose={() => setConfirmModalDetails({ ...confirmModalDetails, isOpen: false })}
        onConfirm={confirmModalDetails.onConfirm}
        title={confirmModalDetails.title}
        message={confirmModalDetails.message}
        confirmText={confirmModalDetails.confirmText}
        cancelText={confirmModalDetails.cancelText}
        type={confirmModalDetails.type}
      />

      <Modal isOpen={modalDetails.isOpen} onClose={modalDetails.onClose}title={modalDetails.title} size="xl">
        <ModalBody>
          {/* <div className="mb-4">
            <input type="text" id="EmployeeId" placeholder="Enter Employee ID" className="w-full p-2 border border-gray-300 rounded" />
          </div>
          <div className="flex justify-end space-x-2">
            <button
              onClick={modalDetails.onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-100"
            >
              {modalDetails.cancelText}
            </button>
            <button
              onClick={modalDetails.onConfirm}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              {modalDetails.confirmText}
            </button>
          </div> */}

          <UpdateOnboardingEntry initialData={modalDetails.data} showToast={showToast} setModalDetails={setModalDetails} fetchSubmissions={fetchSubmissions}  />
        </ModalBody>
      </Modal>
      <Modal isOpen={documentPreviewModal.isOpen} onClose={documentPreviewModal.onClose} title={documentPreviewModal.title} size="xl">
        <ModalBody>
          <DocumentPreview eid={documentPreviewModal.eid} setMergedFile={setMergedFile} />
        </ModalBody>
        <ModalFooter>
          <button
            onClick={documentPreviewModal.onClose}
            className="px-4 py-2 text-white bg-red-500 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          {/* <button
            onClick={documentPreviewModal.onConfirm}
            disabled={!mergedFile}
            className="px-4 py-2 hover:text-gray-700 text-white bg-blue-500 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Process Onboarding
          </button> */}
        </ModalFooter>
      </Modal>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-600 to-green-300 bg-clip-text text-transparent">
            Employee Onboarding Portal
          </h1>

          <p className="mt-2 text-gray-600">
            Manage employee onboarding process efficiently
          </p>
        </div>

        {/* Form Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              Create New Onboarding Entry
            </h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Offer Letter Reference */}
              <div>
                <label
                  htmlFor="offerLetterRef"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Offer Letter Reference <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="offerLetterRef"
                  name="offerLetterRef"
                  value={formData.offerLetterRef}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., OL-2025-001"
                />
              </div>

              {/* First Name */}
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter first name"
                />
              </div>

              {/* Middle Name */}
              <div>
                <label
                  htmlFor="middleName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Middle Name
                </label>
                <input
                  type="text"
                  id="middleName"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter middle name"
                />
              </div>

              {/* Last Name */}
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter last name"
                />
              </div>

              {/* Mobile */}
              <div>
                <label
                  htmlFor="mobile"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+1-234-567-8900"
                />
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="employee@company.com"
                />
              </div>
            </div>

            {/* Remark */}
            <div className="mt-6">
              <label
                htmlFor="remark"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Remark
              </label>
              <textarea
                id="remark"
                name="remark"
                value={formData.remark}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Additional notes or comments"
              />
            </div>

            {/* Submit Button */}
            <div className="mt-6">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating Entry...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Create Onboarding Entry
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Status Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Onboarding Status
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Track all onboarding entries and their progress
              </p>
            </div>
            <button
              onClick={fetchSubmissions}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
              ) : (
                "Refresh"
              )}
            </button>
          </div>

          <div className="overflow-x-auto overflow-y-auto h-[calc(100vh-400px)]">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">
                  Loading submissions...
                </span>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reference
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Progress
                    </th> */}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joining Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Verification Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                    {user.is_superuser&&<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      View & Process Documents
                    </th>}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {submissions.map((submission) => (
                    <tr key={submission.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {submission.offer_letter_reference}
                        </div>
                        {submission.remark && (
                          <div className="text-sm text-gray-500 truncate max-w-32">
                            {submission.remark}
                          </div>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {submission.eid}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {submission.full_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {submission.email_address}
                        </div>
                        <div className="text-sm text-gray-500">
                          {submission.mobile_number}
                        </div>
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(submission.status)}>
                          {getStatusIcon(submission.status)}
                          {getStatusText(submission.status)}
                        </span>
                      </td> */}
                      {/* <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: submission.status }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-600">
                            {submission.status}
                          </span>
                        </div>
                      </td> */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(
                          submission.date_of_joining
                        ).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </td>
                      <td className="px-6 py-6 whitespace-nowrap text-sm text-gray-500">
                        {submission.verification_code}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap flex items-center justify-between space-x-2">
                        {/* Track Status */}
                        {user.is_superuser&&<button
                          onClick={() =>
                            setEid(submission.offer_letter_reference)
                          }
                          className="p-2 rounded-full text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-200"
                          title="Track Status"
                        >
                          <Eye className="w-4 h-4" />
                        </button>}

                        {/* Send to Mail */}
                        <button
                          onClick={() => handleModal({
                            isOpen: true,
                            title: "Confirm Action",
                            message: "Are you sure you want to send welcome mail?",
                            confirmText: "Confirm",
                            cancelText: "Cancel",
                            type: "warning",
                            onConfirm: () => {sendToMail(submission.offer_letter_reference, submission.first_name + (submission.middle_name? " " + submission.middle_name : "") + " " + submission.last_name, submission.email_address, submission.verification_code)},
                            onClose: () => {handleModal({isOpen: false})},
                          })}
                          className="p-2 rounded-full text-gray-600 hover:text-green-600 hover:bg-green-50 transition-colors duration-200"
                          title="Send to Mail"
                        >
                          {sendEmailLoading &&
                          eid === submission.offer_letter_reference ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <MailIcon className="w-4 h-4" />
                          )}
                        </button>

                        {/* Update */}
                        <button
                          onClick={() => setModalDetails({
                            isOpen: true,
                            title: "Update Reference ID with Employee ID",
                            message: "",
                            confirmText: "Update",
                            cancelText: "Cancel",
                            type: "info",
                            onConfirm: () => {handleUpdate(submission.offer_letter_reference)},
                            onClose: () => {setModalDetails({isOpen: false})},
                            data: submission,
                          })}
                          className="p-2 rounded-full text-gray-600 hover:text-yellow-600 hover:bg-yellow-50 transition-colors duration-200"
                          title="Update Reference ID with Employee ID"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>

                        {/* Delete */}
                        {/* <button
                          onClick={() => handleModal({
                            isOpen: true,
                            title: "Confirm Action",
                            message: "Are you sure you want to delete?",
                            confirmText: "Confirm",
                            cancelText: "Cancel",
                            type: "warning",
                            onConfirm: () => {handleModal({isOpen: false})},
                            onClose: () => {handleModal({isOpen: false})},
                          })}
                          className="p-2 rounded-full text-gray-600 hover:text-red-600 hover:bg-red-50 transition-colors duration-200"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button> */}
                      </td>
                      {user.is_superuser&&<td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => setDocumentPreviewModal({
                            isOpen: true,
                            title: "Document Preview",
                            message: "Are you sure you want to proceed?",
                            confirmText: "Proceess",
                            cancelText: "Cancel",
                            type: "info",
                            eid: submission.eid?submission.eid:submission.offer_letter_reference,
                            onConfirm: () => {mergeAndUpload(submission.eid?submission.eid:submission.offer_letter_reference); setMergedFile(null)},
                            onClose: () => {setDocumentPreviewModal({isOpen: false}); setMergedFile(null)},
                          })}
                          className="p-2 rounded-full text-yellow-400 hover:text-yellow-600 hover:bg-yellow-50 transition-colors duration-200"
                          title="View & Process Documents"
                        >
                          <ScanEyeIcon className="w-4 h-4"/>
                        </button>
                        <button 
                          onClick={()=>handleDownloadAll(submission.eid?submission.eid:submission.offer_letter_reference)}
                          className="p-2 rounded-full text-green-400 hover:text-green-600 hover:bg-green-50 transition-colors duration-200"
                          title="Download all documents"
                        >
                          <DownloadIcon className="w-4 h-4"/>
                        </button>
                      </td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {eid && (
            <ApplicationTracker
              eid={eid}
              setEid={setEid}
              Name={
                submissions.filter(
                  (submission) => submission.offer_letter_reference === eid
                )[0]?.full_name
              }
            />
          )}

          {submissions.length === 0 && !isLoading && (
            <div className="text-center py-12">
              <div className="text-gray-500">
                <p>No onboarding entries found.</p>
                <p className="text-sm">
                  Create your first onboarding entry using the form above.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HROnboardingPanel;







const DocumentPreview = ({ eid, setMergedFile }) => {
  const API_BASE_URL = `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/emp-documnets/docs`;
  const GET_URLS_ENDPOINT = `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/emp-documnets/docs/get-urls`;
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const resp = await fetch(`${GET_URLS_ENDPOINT}?eid=${eid}`);
        if (!resp.ok) throw new Error("Failed to fetch document URLs");
        const data = await resp.json();

        const docsArray = Object.entries(data);
        const fetchedDocs = [];

        for (const [docType, url] of docsArray) {
          try {
            const fileResp = await fetch(url);
            const blob = await fileResp.blob();
            const objectUrl = URL.createObjectURL(blob);

            const isPDF = blob.type === "application/pdf";
            const isImage = blob.type.startsWith("image/");

            fetchedDocs.push({
              docType,
              objectUrl,
              isPDF,
              isImage,
            });
          } catch (err) {
            console.error("Failed to load document:", docType, err);
          }
        }

        setDocuments(fetchedDocs);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [eid]);

  const handleDelete = async (docType) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    const filename = `${eid}_${docType}.pdf`;

    try {
      setDeleting(docType);
      const resp = await fetch(`${API_BASE_URL}?eid=${eid}&file=${filename}`, {
        method: "DELETE",
      });
      if (!resp.ok) throw new Error("Failed to delete document");

      setDocuments((prev) => prev.filter((d) => d.docType !== docType));
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleting(null);
    }
  };

  const downloadMergedFile = async () => {
    try {
      const resp = await fetch(`${API_BASE_URL}/merge?eid=${eid}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}` // if you need bearer token
        }
      });
  
      if (!resp.ok) {
        throw new Error('Failed to fetch file');
      }
  
      // Convert response to blob
      const fileBlob = await resp.blob();
  
      // You can store the blob in your state variable
      setMergedFile(fileBlob);
  
      // Optional: If you want to preview or create object URL
      const fileUrl = URL.createObjectURL(fileBlob);
      console.log('File URL:', fileUrl);
  
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };
  useEffect(() => {
    downloadMergedFile();
  }, []);
  


  if (loading)
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-blue-500 w-8 h-8" />
      </div>
    );

  if (error)
    return <div className="text-red-500 text-center mt-6">❌ {error}</div>;

  if (documents.length === 0)
    return (
      <div className="text-gray-600 text-center mt-6">
        No documents available.
      </div>
    );

  return (
    <div className="h-screen overflow-y-auto bg-gray-100 p-6">
      <h2 className="text-2xl font-semibold mb-6 text-gray-800 text-center">
        Employee Documents Preview
      </h2>

      <div className="flex flex-col gap-8">
        {documents.map(({ docType, objectUrl, isPDF, isImage }) => (
          <div
            key={docType}
            className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden"
          >
            {/* Header */}
            <div className="flex justify-between items-center p-4 bg-gray-50 border-b">
              <div className="flex items-center gap-2">
                {isImage ? (
                  <ImageIcon className="w-5 h-5 text-blue-500" />
                ) : (
                  <FileText className="w-5 h-5 text-blue-500" />
                )}
                <span className="font-medium text-gray-700 text-sm uppercase tracking-wide">
                  {docType.replace(/_/g, " ")}
                </span>
              </div>

              <button
                onClick={() => handleDelete(docType)}
                disabled={deleting === docType}
                className={`text-red-500 hover:text-red-600 transition ${
                  deleting === docType ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {deleting === docType ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Trash2 className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* Preview */}
            <div className="bg-gray-50 p-3 flex justify-center items-center">
              {isPDF ? (
                <iframe
                  src={objectUrl}
                  title={docType}
                  className="w-full h-[700px] border-0 rounded-md shadow-inner"
                />
              ) : isImage ? (
                <img
                  src={objectUrl}
                  alt={docType}
                  className="max-h-[700px] w-auto object-contain rounded-md shadow-inner"
                />
              ) : (
                <div className="text-gray-500 italic p-6">
                  Preview not available for this file type.
                </div>
              )}
            </div>

            <div className="p-3 text-xs text-gray-500 text-center bg-gray-50 border-t">
              {isPDF ? "PDF Document" : isImage ? "Image File" : "Other Format"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};






const UpdateOnboardingEntry = ({ initialData, showToast, setModalDetails, fetchSubmissions }) => {
  const [formData, setFormData] = useState(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const updateEmployeeDetails = async (refID, data) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/pre-onboarding/${refID}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify(data),
        }
      );
      if (!res.ok) throw new Error("Failed to update");
      showToast("Details updated successfully!", "success");
      setModalDetails({ isOpen: false });
      fetchSubmissions();
      return await res.json();
    } catch (error) {
      console.error(error);
      showToast("Failed to update details!", "error");
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await updateEmployeeDetails(formData.offer_letter_reference, formData);
    setIsSubmitting(false);
  };
  return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              Create New Onboarding Entry
            </h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Offer Letter Reference */}
              <div>
                <label
                  htmlFor="offer_letter_reference"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Offer Letter Reference <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="offer_letter_reference"
                  name="offer_letter_reference"
                  value={formData.offer_letter_reference}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., OL-2025-001"
                />
              </div>

              {/* First Name */}
              <div>
                <label
                  htmlFor="first_name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter first name"
                />
              </div>

              {/* Middle Name */}
              <div>
                <label
                  htmlFor="middle_name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Middle Name
                </label>
                <input
                  type="text"
                  id="middle_name"
                  name="middle_name"
                  value={formData.middle_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter middle name"
                />
              </div>

              {/* Last Name */}
              <div>
                <label
                  htmlFor="last_name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter last name"
                />
              </div>

              {/* Mobile */}
              <div>
                <label
                  htmlFor="mobile_number"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="mobile_number"
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+1-234-567-8900"
                />
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email_address"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email_address"
                  name="email_address"
                  value={formData.email_address}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="employee@company.com"
                />
              </div>
            </div>

            {/* Remark */}
            <div className="mt-6">
              <label
                htmlFor="remark"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Remark
              </label>
              <textarea
                id="remark"
                name="remark"
                value={formData.remark}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Additional notes or comments"
              />
            </div>

        {/* Submit */}
        <div className="mt-6">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="inline-flex items-center px-6 py-3 text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Updating...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" /> Update Entry
              </>
            )}
          </button>
        </div>
          </div>
        </div>
  )
}